import { useMemo, useRef } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useShapeTextWrapContext(ctx: ShapeManagerStateApi) {
  const { elementZIndexMap, shapes } = ctx

  /**
   * Text-wrap context handed to every text frame's layout (Phase 5a — see
   * `lib/shapes/shape-text-wrap-bands.ts`).
   *
   * Keyed on a signature of the wrap **sources** rather than on `shapes` itself. Every text
   * frame's flow memo depends on this object's identity, so keying it on the whole `shapes` array
   * would re-run the scanline + contour-band computation for every text frame on every pointer
   * move of any shape — including shapes that are not wrap sources at all. The frame's own
   * geometry is already a dependency of that memo, so the two together cover exactly the cases
   * that can change a wrapped layout.
   *
   * `undefined` when nothing on the spread is a wrap source, which keeps the no-wrap path
   * reference-stable and completely free.
   */
  const wrapSourceSignature = useMemo(() => {
    const geom = (s: ShapeData) => [s.id, s.x, s.y, s.width, s.height, s.rotation ?? 0].join(',')
    const sources = shapes.filter(
      (s) => s.wrapStyle != null && s.wrapStyle !== 'none' && s.hidden !== true
    )

    if (sources.length === 0) return ''

    const parts = sources.map((s) =>
      [
        geom(s),
        s.wrapStyle,
        s.parentGroupId ?? '',
        s.wrapOffsetTop ?? '',
        s.wrapOffsetRight ?? '',
        s.wrapOffsetBottom ?? '',
        s.wrapOffsetLeft ?? '',
      ].join(',')
    )

    // A group source's obstacle is its LIVE union of children (`computeLiveGroupWrapRect`), and a
    // child is not itself a source — so moving a child changes the obstacle without changing
    // anything above. Widen the key to all geometry in that case; correctness over the narrower
    // invalidation, and only when a group is actually a wrap source.
    if (sources.some((s) => isGroupShape(s))) parts.push(shapes.map(geom).join('|'))

    return parts.join('|')
  }, [shapes])

  /**
   * Assigned during render, deliberately **not** in an effect — unlike `shapesRef`, which is. The
   * memo below must see the array from the very render whose signature change triggered it; an
   * effect-assigned ref would still hold the previous array at that moment and every band would be
   * one frame stale. Being a ref also keeps it out of the memo's dependencies, which is the whole
   * point of keying on the signature.
   */
  const shapesForWrapRef = useRef(shapes)

  shapesForWrapRef.current = shapes

  const shapeTextWrapContext = useMemo<ShapeTextWrapContext | undefined>(() => {
    if (wrapSourceSignature === '') return undefined

    const snapshot = shapesForWrapRef.current
    const zIndex = elementZIndexMap

    return { allShapes: snapshot, zIndexOf: (id: string) => zIndex?.get(id) }
  }, [wrapSourceSignature, elementZIndexMap])

  // Assigned during render for the same reason `shapesForWrapRef` is: `getShapeTextWrapContext`
  // is read imperatively, mid-gesture, by callers that must measure with exactly what this render
  // is painting with. An effect-assigned ref would be one frame stale.
  const shapeTextWrapContextRef = useRef(shapeTextWrapContext)

  shapeTextWrapContextRef.current = shapeTextWrapContext

  return { wrapSourceSignature, shapesForWrapRef, shapeTextWrapContext, shapeTextWrapContextRef }
}

export type ShapeTextWrapContextApi = ReturnType<typeof useShapeTextWrapContext>
